import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { debounce } from 'lodash';

/**
 * Custom hook that equalizes the heights of elements using React refs.
 * Uses useLayoutEffect for synchronous measurement and ResizeObserver for watching changes.
 * Manually refined from initial Cursor AI suggested implementation.
 *
 * @param elementRefs - Array of refs to the elements to equalize
 * @param dependencies - Array of dependencies that should trigger re-measurement
 */
export function useEqualHeights(
  elementRefs: React.RefObject<HTMLElement>[],
  dependencies: React.DependencyList = [],
): void {
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const debouncedEqualizeRef = useRef<ReturnType<typeof debounce> | null>(null);
  const isEqualizingRef = useRef(false);
  const elementRefsRef = useRef(elementRefs);

  useEffect(() => {
    elementRefsRef.current = elementRefs;
  }, [elementRefs]);

  const equalizeHeights = useCallback(() => {
    if (isEqualizingRef.current) return;

    const elements = elementRefsRef.current.map((ref) => ref.current).filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    isEqualizingRef.current = true;

    try {
      // Reset heights to auto to get natural height
      elements.forEach((el) => {
        el.style.height = 'auto';
      });

      // Force a reflow to ensure heights are recalculated
      if (elements.length > 0) {
        void elements[0].offsetHeight;
      }

      // Find the maximum height using scrollHeight which includes all content including padding
      let maxHeight = 0;
      elements.forEach((el) => {
        const height = el.scrollHeight;
        if (height > maxHeight) {
          maxHeight = height;
        }
      });

      // Only update heights if they actually need to change
      if (maxHeight > 0) {
        elements.forEach((el) => {
          const currentHeight = el.style.height;
          const newHeight = `${maxHeight}px`;
          if (currentHeight !== newHeight) {
            el.style.height = newHeight;
          }
        });
      }
    } finally {
      isEqualizingRef.current = false;
    }
  }, []); // No dependencies - uses ref instead

  // Use useLayoutEffect for synchronous measurement before paint
  useLayoutEffect(() => {
    equalizeHeights();
  }, [equalizeHeights, ...dependencies]);

  useEffect(() => {
    const elements = elementRefs.map((ref) => ref.current).filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    if (debouncedEqualizeRef.current) {
      debouncedEqualizeRef.current.cancel();
    }
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    // Create debounced version with requestAnimationFrame to prevent ResizeObserver loops
    // The debounce delays execution, and RAF ensures it runs after the current frame
    // Using a stable equalizeHeights callback means this only recreates when dependencies change
    debouncedEqualizeRef.current = debounce(() => {
      requestAnimationFrame(() => {
        equalizeHeights();
      });
    }, 16); // ~1 frame at 60fps

    resizeObserverRef.current = new ResizeObserver(() => {
      debouncedEqualizeRef.current?.();
    });

    const observer = resizeObserverRef.current;

    elements.forEach((el) => {
      observer.observe(el);
    });

    return () => {
      debouncedEqualizeRef.current?.cancel();
      observer.disconnect();
      resizeObserverRef.current = null;
      debouncedEqualizeRef.current = null;
      isEqualizingRef.current = false;
    };
  }, [elementRefs, equalizeHeights, ...dependencies]);
}
